// Tabs: a tab strip, `segmented` (pills: a filter over one list) or `underline` (a page's
// sections), with the arrow-key roving the tab pattern needs. The panels are the caller's: render
// the selected one in a TabPanel. Ported from the Humint Assistant's components/ui/tabs.tsx.
import { useRef, type ComponentProps, type KeyboardEvent } from 'react';
import { cn } from './cn';

export interface TabItem {
  id: string;
  label: string;
  /** A count or short status after the label. */
  hint?: string;
}

export interface TabsProps extends Omit<ComponentProps<'div'>, 'onChange'> {
  tabs: TabItem[];
  value: string;
  onValueChange: (id: string) => void;
  /** Accessible name for the strip. */
  label: string;
  variant?: 'segmented' | 'underline';
  /** Prefix for the tab and panel ids (several strips on one page). */
  idPrefix?: string;
}

export function Tabs({ tabs, value, onValueChange, label, variant = 'segmented', idPrefix = 'tab', className, ...props }: TabsProps) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const segmented = variant === 'segmented';

  function onKeyDown(event: KeyboardEvent) {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const index = tabs.findIndex((t) => t.id === value);
    const next = tabs[(index + step + tabs.length) % tabs.length];
    if (!next) return;
    onValueChange(next.id);
    refs.current[next.id]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cn(
        segmented
          ? 'inline-flex max-w-full flex-wrap gap-1 rounded-full border border-hairline bg-page p-1'
          : 'flex gap-0.5 overflow-x-auto border-b border-hairline',
        className,
      )}
      {...props}
    >
      {tabs.map((t) => {
        const selected = t.id === value;
        return (
          <button
            key={t.id}
            ref={(node) => {
              refs.current[t.id] = node;
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-${t.id}`}
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel-${t.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onValueChange(t.id)}
            className={cn(
              'cursor-pointer whitespace-nowrap text-[12.5px] font-medium coarse:min-h-10',
              segmented
                ? cn('rounded-full px-3 py-1', selected ? 'bg-ink-display text-page' : 'text-ink-muted hover:text-ink-body')
                : cn(
                    '-mb-px border-b-2 px-3 pb-2.5 pt-2',
                    selected ? 'border-accent font-semibold text-ink-display' : 'border-transparent text-ink-body hover:text-ink-display',
                  ),
            )}
          >
            {t.label}
            {t.hint && <span className={cn('ml-1.5 font-mono text-[11px]', segmented ? 'opacity-75' : 'text-ink-muted')}>{t.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** The region a tab controls. Render only the selected one. */
export function TabPanel({ id, idPrefix = 'tab', className, ...props }: ComponentProps<'div'> & { id: string; idPrefix?: string }) {
  return <div role="tabpanel" id={`${idPrefix}-panel-${id}`} aria-labelledby={`${idPrefix}-${id}`} className={className} {...props} />;
}
